/**
 * Email variant shape and bounds, kept OUT of the .tsx so the editor module
 * exports only its component — `react-refresh/only-export-components` is a
 * lint error under the portal's `--max-warnings 0` gate.
 */

/** Mirrors the strata service (a longer value is a 400). */
export const EMAIL_SUBJECT_MAX = 255;
export const EMAIL_PARAM_COUNT_MAX = 25;
export const EMAIL_PARAM_VALUE_MAX = 2000;

/**
 * Where inbox lists truncate. Past this the subject still SENDS, it just stops
 * being read — guidance, not a rule, so it is a soft warning like the push
 * editor's visible-length hints.
 */
export const EMAIL_SUBJECT_VISIBLE = 70;

/**
 * Params are edited as an ORDERED LIST of pairs, not as an object: a
 * key-value editor backed by an object loses the row the moment its key is
 * blanked mid-typing, and re-keying an existing row would silently drop the
 * previous entry.
 */
export interface EmailParamRow {
  key: string;
  value: string;
}

/**
 * `brevoTemplateId` is a STRING in form state. The channel-config list may be
 * empty for a brand (contract §5 makes it informational), so the field has to
 * accept free numeric entry — and an in-progress "" or "16" must not become
 * NaN or 16 before the operator has finished typing.
 */
export interface EmailVariant {
  subject: string;
  brevoTemplateId: string;
  params: EmailParamRow[];
}

export const EMPTY_EMAIL_VARIANT: EmailVariant = {
  subject: '',
  brevoTemplateId: '',
  params: [],
};

/** The first compose-time problem, in the operator's words. */
export function emailVariantError(variant: EmailVariant): string | null {
  if (!variant.subject.trim()) {
    return 'Please enter an email subject';
  }
  if (variant.subject.length > EMAIL_SUBJECT_MAX) {
    return `Email subject exceeds ${EMAIL_SUBJECT_MAX} characters`;
  }
  const templateId = Number(variant.brevoTemplateId.trim());
  if (
    !variant.brevoTemplateId.trim() ||
    !Number.isInteger(templateId) ||
    templateId <= 0
  ) {
    return 'Please enter the Brevo template id that holds the email body (a positive whole number)';
  }
  const unnamed = variant.params.findIndex(
    (param) => !param.key.trim() && param.value.trim().length > 0,
  );
  if (unnamed !== -1) {
    // A row with a value but no name is dropped by `toEmailVariantPayload`,
    // and silently: the operator would see their merge field simply not appear
    // in the delivered email with nothing having warned them.
    return `Brevo param ${unnamed + 1} has a value but no name — name it or clear the value`;
  }
  const named = variant.params.filter((param) => param.key.trim().length > 0);
  if (named.length > EMAIL_PARAM_COUNT_MAX) {
    return `An email variant carries at most ${EMAIL_PARAM_COUNT_MAX} Brevo params`;
  }
  const oversized = named.find(
    (param) => param.value.length > EMAIL_PARAM_VALUE_MAX,
  );
  if (oversized) {
    return `Brevo param ${oversized.key.trim()} exceeds ${EMAIL_PARAM_VALUE_MAX} characters`;
  }
  const keys = named.map((param) => param.key.trim());
  const duplicate = keys.find((key, index) => keys.indexOf(key) !== index);
  if (duplicate) {
    // Caught here rather than server-side: the object the payload builds would
    // silently keep only the last one, and the operator would never see which
    // value actually shipped.
    return `Brevo param ${duplicate} is listed twice`;
  }
  return null;
}

export function isEmailVariantComplete(variant: EmailVariant): boolean {
  return emailVariantError(variant) === null;
}

/**
 * Form state → the send payload the strata DTO expects.
 *
 * Rows with a blank key are dropped, but only EMPTY ones ever reach here: a
 * blank key with a value is refused by `emailVariantError` first, precisely
 * because dropping it would be invisible. An untouched "Add Param" row is not
 * a param, and sending `"": "x"` would be a 400 whose cause the operator
 * cannot see.
 */
export function toEmailVariantPayload(variant: EmailVariant): {
  subject: string;
  brevoTemplateId: number;
  params: Record<string, string>;
} {
  const params: Record<string, string> = {};
  for (const param of variant.params) {
    const key = param.key.trim();
    if (key) {
      params[key] = param.value;
    }
  }
  return {
    subject: variant.subject.trim(),
    brevoTemplateId: Number(variant.brevoTemplateId.trim()),
    params,
  };
}
